"use client";

import { Sparkles } from "lucide-react";
import type {
  ReleaseNote,
  ReleaseNoteHorizon,
  ReleaseNotesPayload,
} from "@/lib/api/api-client";

interface NewsPanelProps {
  data: ReleaseNotesPayload | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

const HORIZON_ORDER: ReleaseNoteHorizon[] = ["now", "next", "later"];

const HORIZON_LABEL: Record<ReleaseNoteHorizon, string> = {
  now: "En cours",
  next: "Prochainement",
  later: "Plus tard",
};

const HORIZON_DOT: Record<ReleaseNoteHorizon, string> = {
  now: "var(--color-accent)",
  next: "var(--color-accent-warm)",
  later: "var(--color-text-muted)",
};

/** Compact French relative date ("aujourd'hui", "il y a 3 jours"). */
function relativeDate(iso: string | null): string | null {
  if (iso === null) {
    return null;
  }

  const then = new Date(iso).getTime();

  if (Number.isNaN(then)) {
    return null;
  }

  const days = Math.floor((Date.now() - then) / 86_400_000);

  if (days <= 0) {
    return "aujourd'hui";
  }

  if (days === 1) {
    return "hier";
  }

  if (days < 7) {
    return `il y a ${days} jours`;
  }

  if (days < 30) {
    const weeks = Math.floor(days / 7);

    return weeks === 1 ? "il y a 1 semaine" : `il y a ${weeks} semaines`;
  }

  const months = Math.floor(days / 30);

  return months === 1 ? "il y a 1 mois" : `il y a ${months} mois`;
}

function NoteCard({ note, date }: { note: ReleaseNote; date?: string | null }) {
  return (
    <article
      className="rounded-xl p-3.5"
      style={{
        backgroundColor: "rgba(255,255,255,0.03)",
        border: "1px solid var(--color-border-subtle)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-[14px] font-semibold leading-snug text-text-primary">
          {note.title}
        </h4>
        {date && (
          <span className="shrink-0 text-[11px] text-text-muted whitespace-nowrap pt-0.5">
            {date}
          </span>
        )}
      </div>
      <p className="mt-1 text-[12px] leading-relaxed text-text-secondary whitespace-pre-line">
        {note.description}
      </p>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div
      className="h-[78px] rounded-xl animate-pulse"
      style={{
        backgroundColor: "rgba(255,255,255,0.03)",
        border: "1px solid var(--color-border-subtle)",
      }}
    />
  );
}

export function NewsPanel({ data, loading, error, onRetry }: NewsPanelProps) {
  if (loading) {
    return (
      <div className="flex flex-col gap-3 px-6 py-6">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <p className="text-[13px] text-text-secondary">
          Impossible de charger les nouveautés.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="h-9 px-4 rounded-lg text-[13px] font-medium text-text-primary cursor-pointer transition-colors duration-150 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{ border: "1px solid var(--color-border-subtle)" }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  const recent = data?.recent ?? [];
  const roadmap = data?.roadmap;
  const hasRoadmap =
    roadmap !== undefined &&
    HORIZON_ORDER.some((h) => (roadmap[h]?.length ?? 0) > 0);

  return (
    <div className="flex flex-col gap-7 px-6 py-6 overflow-y-auto">
      {/* Section A — Récemment ajouté */}
      <section>
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          Récemment ajouté
        </h3>
        {recent.length > 0 ? (
          <div className="flex flex-col gap-3">
            {recent.map((note) => (
              <NoteCard key={note.id} note={note} date={relativeDate(note.releasedAt)} />
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-text-secondary">
            Les premières nouveautés arrivent.
          </p>
        )}
      </section>

      {/* Section B — Bientôt disponible (roadmap priorisée par horizon) */}
      <section>
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          Bientôt disponible
        </h3>
        {hasRoadmap ? (
          <div className="flex flex-col gap-5">
            {HORIZON_ORDER.map((horizon) => {
              const items = roadmap?.[horizon] ?? [];

              if (items.length === 0) {
                return null;
              }

              return (
                <div key={horizon}>
                  <div className="mb-2 flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block w-[7px] h-[7px] rounded-full"
                      style={{ background: HORIZON_DOT[horizon] }}
                    />
                    <span className="text-[12px] font-medium text-text-secondary">
                      {HORIZON_LABEL[horizon]}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {items.map((note) => (
                      <NoteCard key={note.id} note={note} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <Sparkles size={18} className="text-text-muted" />
            <p className="text-[12px] text-text-secondary">
              Rien d&apos;annoncé pour l&apos;instant, reviens vite.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
